import moment from 'moment';

export const useDateHelpers = () => {
    /**
     * Format a date string to '11:12am on November 3, 2024' format
     * @param dateString - ISO date string
     * @returns formatted string or 'N/A' if invalid
     */
    const formatTimeOnDate = (dateString: string): string => {
        if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
            return 'N/A';
        }
        const date = moment.utc(dateString);
        if (!date.isValid()) {
            return 'N/A';
        }
        return date.local().format('h:mma [on] MMMM D, YYYY');
    };
    /**
     * Format a date string to "June 17th, 2025 2:25pm" format
     * @param dateString - ISO date string
     * @returns formatted date string or "N/A" if invalid
     */
    const formatDate = (dateString: string, format: string = 'MMMM Do, YYYY h:mma'): string => {
        // Check for empty, null, undefined, or "N/A" values
        if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
            return 'N/A';
        }

        // Try to create a moment object and validate it
        const date = moment.utc(dateString);
        if (!date.isValid()) {
            return 'N/A';
        }

        return date.local().format(format);
    };

    /**
     * Format a date string to "5/12/25 4:02pm" format
     * @param dateString - ISO date string
     * @returns formatted date string or "N/A" if invalid
     */
    const formatDateWithoutWord = (dateString: string, format: string = 'M/D/YY h:mma'): string => {
        if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
            return 'N/A';
        }
        const date = moment.utc(dateString);
        if (!date.isValid()) {
            return 'N/A';
        }
        return date.local().format(format);
    };

    /**
     * Format a date string to date only, without the time (e.g., "5/12/25")
     * @param dateString - ISO date string
     * @param format - Optional format string (default: 'M/D/YY')
     * @returns formatted date string or "N/A" if invalid
     */
    const formatDateOnly = (dateString: string, format: string = 'M/D/YY'): string => {
        if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
            return 'N/A';
        }
        const date = moment.utc(dateString);
        if (!date.isValid()) {
            return 'N/A';
        }
        return date.local().format(format);
    };


    /**
     * Format a date string to relative time (e.g., "2 hours ago", "3 days ago")
     * @param dateString - ISO date string
     * @returns relative time string
     */
    const formatRelativeTime = (dateString: string): string => {
        if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
            return '';
        }

        const date = moment(dateString);
        if (!date.isValid()) {
            return '';
        }

        return date.fromNow();
    };

    /**
     * Format a date string to short date format (e.g., "Jun 17, 2025")
     * @param dateString - ISO date string
     * @returns short date string
     */
    const formatShortDate = (dateString: string): string => {
        if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
            return '';
        }

        const date = moment(dateString);
        if (!date.isValid()) {
            return '';
        }

        return date.format('MMM D, YYYY');
    };

    /**
     * Format a date string to time only (e.g., "2:25 PM")
     * @param dateString - ISO date string
     * @returns time string
     */
    const formatTime = (dateString: string): string => {
        if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
            return '';
        }

        const date = moment(dateString);
        if (!date.isValid()) {
            return '';
        }

        return date.format('h:mm A');
    };

    /**
     * Check if a date is today
     * @param dateString - ISO date string
     * @returns boolean
     */
    const isToday = (dateString: string): boolean => {
        if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
            return false;
        }

        const date = moment(dateString);
        if (!date.isValid()) {
            return false;
        }

        return date.isSame(moment(), 'day');
    };

    /**
     * Check if a date is in the past
     * @param dateString - ISO date string
     * @returns boolean
     */
    const isPast = (dateString: string): boolean => {
        if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
            return false;
        }

        const date = moment(dateString);
        if (!date.isValid()) {
            return false;
        }

        return date.isBefore(moment());
    };

    return {
        formatDate,
        formatRelativeTime,
        formatShortDate,
        formatTime,
        isToday,
        isPast,
        formatTimeOnDate,
        formatDateWithoutWord,
        formatDateOnly
    };
};
